/* eslint-disable */

"use client";

import type React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { getRichTextOptions, getRichTextReactOptions, resolveHref } from "../functions/richText";
import { handleButtons, resolvePdpLinkEntry } from "@/lib/utils";
import { useNinetailed } from "@ninetailed/experience.js-react";
import { useEcomStore } from "@/context/ecom/EcomContext";
import markdownit from "markdown-it";
import { ProductDetails, PricingCard, LineItem, DatalayerCard } from "@/types/ecom";
import { getPrice, addToCart } from "../api";
import { productsEndpoint } from "@/lib/config/environments";
import { addToCartSelectedProductCard } from "../PdpMethods";
import { applyCreditProductMapping } from "@/lib/FlyoutCart/lineItemHelpers";
import { refreshAuthTokenAsync } from "@/lib/refreshAuthToken";
import getConfigTID from "@/lib/getConfigTID";
import { labels as defaultLabels } from "../constants/constants";
import { SimpleLink } from "@/types/SimpleLink";
import type { ProductDetailLabels, ProductDetailCreditsLabels } from "@/queries/productDetailQueries";
import type { EcomErrorMessages } from "@/queries/ecomErrorMessagesQueries";
import { pushDataLayerEvent } from "@/components/client/Datalayer/utilities";
import { useLogin } from "@/context/login/LoginContext";

// Fallback Trimble Credits tiers when a PDP has no linked
// Trimble Credits Pricing Cards entry (or incomplete values).
// Each tier becomes a separate pricing card. Price is unit amount × credits.
// Example: 10,000 credits @ $0.01 = $100
const DEFAULT_CREDITS_TIERS = [10000, 50000, 100000] as const;

/** Matches Contentful `featuredCard` checkbox option labels (and card order). */
const CREDITS_TIER_LABELS = ["Top Pricing Card", "Middle Pricing Card", "Bottom Pricing Card"] as const;
interface TrimbleCreditsPricingCards {
  entryTitle?: string | null;
  topPricingCard?: string | number | null;
  middlePricingCard?: string | number | null;
  bottomPricingCard?: string | number | null;
  featuredCard?: (string | null)[] | null;
}

/** Top → Middle → Bottom credit amounts from CMS, or the default tiers. */
const resolveCreditsTiers = (cards?: TrimbleCreditsPricingCards | null): number[] => {
  if (!cards) return [...DEFAULT_CREDITS_TIERS];
  const top = Number(cards.topPricingCard);
  const middle = Number(cards.middlePricingCard);
  const bottom = Number(cards.bottomPricingCard);
  const allValid = top > 0 && middle > 0 && bottom > 0;
  if (allValid) return [top, middle, bottom];
  return [...DEFAULT_CREDITS_TIERS];
};

/**
 * "Most Popular" only when Featured Card checkboxes include that tier.
 * If no CMS entry is linked, preserve the legacy middle-tier badge.
 * If an entry exists but nothing is checked, no card is featured.
 */
const isCreditsTierFeatured = (cards: TrimbleCreditsPricingCards | null | undefined, tierIndex: number): boolean => {
  // No linked CMS entry → keep the old "middle card is featured" behavior.
  if (!cards) {
    const middleIndex = Math.floor(DEFAULT_CREDITS_TIERS.length / 2);
    return tierIndex === middleIndex;
  }
  const featured = (cards.featuredCard ?? []).filter(Boolean);
  if (featured.length === 0) return false;
  return featured.includes(CREDITS_TIER_LABELS[tierIndex]);
};

/** PDP accordion rows after formatting the rich text */
interface AccordionItem {
  headline?: string | null;
  description?: string | null;
}

/** Raw Contentful accordion row (rich text document in `description.json`). */
interface AccordionRichTextItem {
  headline?: string;
  description?: {
    json: any;
  } | null;
}
interface ImagesCollectionItem {
  caption?: string | null;
  image?: {
    url?: string | null;
  } | null;
  thumbnailUrl?: string | null;
  altText?: string | null;
}
interface Section {
  title?: string | null;
  description?: {
    json: any;
  } | null;
  linkCollection?: {
    items: SimpleLink[];
  } | null;
  itemsCollection?: {
    items: AccordionRichTextItem[];
  } | null;
}
interface InlineMediaVideoItem {
  videoType?: string | null;
  videoReferenceType?: string | null;
  videoCode?: string | null;
  videoThumbnail?: {
    image?: {
      url?: string | null;
    } | null;
    altText?: string | null;
  } | null;
  videoURL?: string | null;
}
const resolveInlineMediaVideoUrl = (media?: InlineMediaVideoItem | null): string | undefined => {
  if (!media) return undefined;
  if (media.videoReferenceType === "Video Code") {
    return media.videoCode ?? media.videoURL ?? undefined;
  }
  if (media.videoReferenceType === "Video URL") {
    return media.videoURL ?? media.videoCode ?? undefined;
  }
  return media.videoURL ?? media.videoCode ?? undefined;
};
interface InlineMediaSection {
  title?: string | null;
  description?: {
    json: any;
  } | null;
  mediaCollection?: {
    items?: InlineMediaVideoItem[] | null;
  } | null;
  buttonsCollection?: {
    items?: any[] | null;
  } | null;
}
interface FormattedInlineMediaButton {
  id?: string;
  primary: boolean;
  label?: string | null;
  destination?: string | null;
  size: "small" | "medium" | "large";
}
interface InlineMediaData {
  eyebrow?: string | null;
  header?: string | null;
  mediaSectionsCollection?: {
    items?: InlineMediaSection[] | null;
  } | null;
}
interface FormattedInlineMediaItem {
  type: "image" | "video";
  assetURL?: string;
  videoURL?: string;
  alt: string;
}
interface FormattedInlineMediaPanel {
  publicTitle?: string | null;
  description?: string | null;
  alignment?: "mediaRight" | "mediaLeft";
  media?: FormattedInlineMediaItem[];
  buttons?: FormattedInlineMediaButton[];
}
interface FormattedInlineMedia {
  eyebrow?: string | null;
  header?: string | null;
  variant: "tabs" | "carousel";
  inlineMediaPanels?: FormattedInlineMediaPanel[];
}
interface DataProps {
  eyebrow?: string | null;
  subheader?: string | null;
  productName?: string | null;
  longDescription?: string | null;
  addToCartLabel?: string | null;
  processingLabel?: string | null;
  needHelpPurchasingText?: string | null;
  needHelpPurchasingLink?: any;
  questionsAndReturnsText?: string | null;
  questionsAndReturnsLink?: any;
  creditPolicyText?: string | null;
  creditPolicyLink?: any;
  regionBlockedUserLinkToLearnMore?: any;
  pim?: ProductDetails | null;
  secondaryProductsData?: (ProductDetails | null)[] | null;
  trimbleCreditsPricingCards?: TrimbleCreditsPricingCards | null;
  chooseYourPlanLabel?: string;
  numberOfLicensesLabel?: string;
  errorMessageForQuantityAt0?: string;
  errorMessageForMaxQuantity?: string;
  regionalAvailability?: string;
  productDescription?: string;
  productOverview?: string;
  productDetailsCollection?: {
    items: AccordionRichTextItem[];
  } | null;
  imagesCollection?: {
    items?: ImagesCollectionItem[] | null;
  };
  sectionsCollection?: {
    items: Section[];
  } | null;
  inlineMedia?: InlineMediaData | null;
  productDetailLabels?: ProductDetailLabels | null;
  featuresAndHighlights?: {
    json?: unknown;
  } | null;
  technicalSpecifications?: {
    json?: unknown;
  } | null;
  pricingAndPlans?: {
    json?: unknown;
  } | null;
  whatsIncluded?: {
    json?: unknown;
  } | null;
  howItWorks?: {
    json?: unknown;
  } | null;
  customDetailTitle?: string | null;
  customDetailDescription?: {
    json?: unknown;
  } | null;
  usageTermsTitle?: string | null;
  usageTermsDescription?: {
    json?: unknown;
    links?: {
      entries?: {
        hyperlink?: any[];
      };
    };
  } | null;
  secondaryLinkText?: string | null;
  secondaryLink?: SimpleLink | null;
}
interface ImageGalleryItem {
  caption?: string | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  altText?: string | null;
}
interface Link {
  text?: string | null;
  onClick: () => void;
  link: {
    url?: string | null;
    text?: string | null;
  };
}
interface CreditPolicyTextAndLink {
  text?: string | null;
  link: {
    url?: string | null;
    text?: string | null;
  };
}
interface AddToCartButton {
  text?: string | null;
  processingText?: string | null;
  onClick: () => void;
}
interface Labels {
  quantityLabel?: string | null;
  chooseYourPlanLabel?: string | null;
  numberOfLicensesLabel?: string | null;
}
interface CustomTab {
  credits?: number;
  onCreditsChange?: (credits: number) => void;
  totalCost?: string;
  onCreditTabChange?: (tab: "packs" | "custom") => void;
}
interface UseProductDetail {
  pricingCards: ProductDetails[];
  pricingDetails: {
    cards: PricingCard[];
  };
  eyebrow?: string | null;
  subheader?: string | null;
  productName?: string | null;
  productDetailList: string;
  images?: ImageGalleryItem[] | null;
  needHelpPurchasing: Link;
  questionsAndReturnsLink: CreditPolicyTextAndLink;
  creditPolicyLink: CreditPolicyTextAndLink;
  regionBlockedUserLink: {
    url?: string | null;
    text?: string | null;
  };
  addToCartButton: AddToCartButton;
  labels: Labels;
  regionAvailability?: string;
  productDescription?: string;
  productOverview?: string;
  productDetails: AccordionItem[];
  sections?: Section[] | null;
  inlineMedia?: FormattedInlineMedia | null;
  creditPage?: boolean;
  customTab?: CustomTab;
  locale?: string;
  // Terra's <ProductDetail> accepts a `content.listImage` ({ src, altText })
  // on credit PDPs (1.1.9-beta.2+). We pass the PIM listImage straight through
  // so the credits hero/card image matches the rest of the PDP imagery.
  listImage?: {
    src: string;
    altText?: string;
  } | null;
  handleReplaceCart: () => void;
  handleCancelReplaceCart: () => void;
  usageTerms?: {
    title?: string | null;
    description?: React.ReactNode | null;
  } | null;
  secondaryLink?: {
    text?: string | null;
    link: {
      text?: string | null;
      url?: string | null;
    };
  };
}
interface UseProductDetailProps {
  data: DataProps;
  labels?: ProductDetailLabels | null;
  creditsLabels?: ProductDetailCreditsLabels | null;
  errorMessages?: EcomErrorMessages | null;
  setLoading: (value: boolean) => void;
  selected: string;
  setSelected: (value: string) => void;
  setIsMaxQuantityError: (value: boolean) => void;
  setErrorMessage: (value: string) => void;
  setError: (value: boolean) => void;
  disabled: boolean;
  setDisabled: (value: boolean) => void;
  count: number;
  setCount: (value: number) => void;
  setRegionBlocked: (value: boolean) => void;
  quantityError: boolean;
  setQuantityError: (value: boolean) => void;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
  setSubscriptionTypeErrorNonMerge: (value: boolean) => void;
  setSubscriptionTypeErrorMerge: (value: boolean) => void;
  setIsOpen: (value: boolean) => void;
  setAddToCartError: (value: boolean) => void;
  setIsCurrencyMismatch: (value: boolean) => void;
  setIsAccountError: (value: boolean) => void;
  setIsRestrictedError: (value: boolean) => void;
  hasCartConflict: boolean;
  setCartConflict: (value: boolean) => void;
  pricingDataForDatalayer: any;
  setPricingDataForDatalayer: (value: any) => void;
  loading: boolean;
  englishPricingCardsForDatalayer: DatalayerCard;
  locale?: string;
}
const useProductDetail = ({
  data,
  labels: labelsProp,
  creditsLabels: creditsLabelsProp,
  errorMessages,
  setLoading,
  selected,
  setSelected,
  setIsMaxQuantityError,
  setErrorMessage,
  setError,
  disabled,
  setDisabled,
  count,
  setCount,
  setRegionBlocked,
  quantityError,
  setQuantityError,
  isProcessing,
  setIsProcessing,
  setSubscriptionTypeErrorNonMerge,
  setSubscriptionTypeErrorMerge: _setSubscriptionTypeErrorMerge,
  setIsOpen,
  setAddToCartError,
  setIsCurrencyMismatch: _setIsCurrencyMismatch,
  setIsAccountError: _setIsAccountError,
  setIsRestrictedError,
  hasCartConflict,
  setCartConflict,
  pricingDataForDatalayer,
  setPricingDataForDatalayer,
  loading,
  englishPricingCardsForDatalayer,
  locale
}: UseProductDetailProps): UseProductDetail => {
  const {
    track
  } = useNinetailed();
  const [state, dispatch] = useEcomStore() ?? [null, () => {
    console.warn("Dispatch called but store not available");
  }];
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];
  const onLogin = useCallback((token: string, authTime: string, idToken: string, refreshToken: string) => {
    loginDispatch?.({
      type: "SET_LOGIN",
      payload: {
        token,
        authTime,
        idToken,
        refreshToken
      }
    });
  }, [loginDispatch]);
  const onLogout = useCallback(() => {
    loginDispatch?.({
      type: "LOG_OUT"
    });
  }, [loginDispatch]);

  // // Memoize TID config so the code_verifier stays stable across re-renders
  const tidConfig = useMemo(() => getConfigTID(), []);
  const initialPricingCards: ProductDetails[] = [data?.pim, ...(data?.secondaryProductsData ?? [])].filter((card): card is ProductDetails => Boolean(card));
  const [pricingCards, setPricingCards] = useState<ProductDetails[]>(initialPricingCards);
  const [isDVW, setIsDVW] = useState(false);
  const [isOwner, setOwner] = useState(false);
  const [isProductUser, setProductUser] = useState(false);
  const [itemInCart, setItemInCart] = useState<LineItem | null>(null);
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // State management for the Trimble Credits custom tab:
  //   - `creditUnitAmount`: The per-credit price returned by the pricing API (e.g., 0.01).
  //   - `customCredits`: The current value selected in the CreditsTracker. 
  // 
  // Note: `customCredits` stays undefined until the pricing API resolves, 
  // at which point we default it to the API's `minPurchaseThreshold`.
  const [creditUnitAmount, setCreditUnitAmount] = useState<number | null>(null);
  const [customCredits, setCustomCredits] = useState<number | undefined>(undefined);
  const richTextOptions = getRichTextOptions(undefined, locale);
  const formatProductDetails = (): AccordionItem[] => {
    const productDetailKeys = ["featuresAndHighlights", "technicalSpecifications", "pricingAndPlans", "whatsIncluded", "howItWorks"] as const;
    const productDetailData: AccordionItem[] = productDetailKeys.map(key => ({
      headline: labelsProp?.[key] ?? defaultLabels?.[key] ?? null,
      description: data?.[key]?.json ? documentToHtmlString(data?.[key]?.json as any, richTextOptions) : null
    }));
    if (data?.customDetailTitle && data?.customDetailDescription) {
      productDetailData.push({
        headline: data.customDetailTitle,
        description: data.customDetailDescription?.json ? documentToHtmlString(data.customDetailDescription.json as any, richTextOptions) : null
      });
    }
    return productDetailData;
  };
  const isAuthTokenValid = (authTime_0: string | number | null | undefined): boolean => {
    if (authTime_0 && process.env.NEXT_PUBLIC_EXPIRE_AUTH_TOKEN !== "true") {
      const minutesRemaining = (Date.now() - Number(authTime_0)) / 60000;
      return minutesRemaining <= 60;
    }
    return false;
  };
  const getTIDAuth = () => {
    const authTime_1 = loginState?.authTime;
    const token_0 = loginState?.token;
    if (isAuthTokenValid(authTime_1) && token_0 && process.env.NEXT_PUBLIC_EXPIRE_AUTH_TOKEN !== "true") {
      return {
        id: token_0
      };
    }
    return null;
  };
  const tokenRefreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const getValidToken = async () => {
    if (!loginState?.token || !tidConfig) return null;
    if (isAuthTokenValid(loginState?.authTime)) return loginState?.token;

    // If a token refresh is already in progress, return the existing promise
    if (tokenRefreshPromiseRef.current) return tokenRefreshPromiseRef.current;

    // Otherwise, start a new token refresh and store the promise
    tokenRefreshPromiseRef.current = (async () => {
      try {
        const authData = await refreshAuthTokenAsync({
          refreshToken: loginState?.refreshToken,
          client_id: tidConfig?.client_id,
          redirect_uri: tidConfig?.redirect_uri,
          code_verifier: tidConfig?.code_verifier,
          path: window?.location?.pathname
        });
        if (authData?.id) {
          onLogin(authData.id, String(authData.time), authData.idTokenForLogout ?? "", authData.refresh_token ?? "");
          return authData.id;
        }

        // If refresh fails, log out the user
        onLogout();
        return null;
      } finally {
        tokenRefreshPromiseRef.current = null;
      }
    })();
    return tokenRefreshPromiseRef.current;
  };
  useEffect(() => {
    if (state?.accounts && state?.activeAccount) {
      if (state?.activeAccount?.role === "ACCOUNT_OWNER" || state?.activeAccount?.role === "SECONDARY_ACCOUNT_OWNER") {
        setOwner(true);
        setProductUser(false);
        if (disabled) {
          setDisabled(false);
        }
        const DVW = state?.activeAccount?.sourceSystems?.find((source: string) => {
          return source == "DVW";
        });
        if (DVW) {
          setIsDVW(true);
          if (disabled) {
            setDisabled(false);
          }
        } else {
          setIsDVW(false);
        }
      } else if (state?.activeAccount?.role === "PRODUCT_USER" || state?.activeAccount?.role === "COMPANY_ADMIN") {
        setProductUser(true);
        setOwner(false);
      }
      // this sends user info to aptrinsic when user is logged in
      if (typeof window.aptrinsic === "function") {
        window.aptrinsic("identify", {
          id: loginState?.user?.tid,
          email: loginState?.user?.email,
          firstName: loginState?.user?.firstName,
          lastName: loginState?.user?.lastName
        }, {
          id: state?.activeAccount?.id,
          name: state?.activeAccount?.name
        });
      }
    }
  }, [state?.activeAccount, state?.accounts]);
  const images = (() => {
    if (data?.imagesCollection?.items?.length) {
      return data.imagesCollection.items.map((image: any) => ({
        caption: image?.caption,
        imageUrl: image?.image?.url,
        thumbnailUrl: image?.image?.url ? image.image.url + "?w=180" : null,
        altText: image?.altText
      }));
    }
    const pimImages = data?.pim?.secondaryImagesCollection?.items;
    if (pimImages?.length) {
      return pimImages.map((image_0: any) => ({
        caption: image_0?.description || image_0?.title,
        imageUrl: image_0?.url,
        thumbnailUrl: image_0?.url ? image_0.url + "?w=180" : null,
        altText: image_0?.title
      }));
    }
    return undefined;
  })();
  const pdpLink = (entry?: any) => resolvePdpLinkEntry(entry, locale) ?? {
    url: null,
    text: null
  };
  const needHelpPurchasing: Link = {
    text: data?.needHelpPurchasingText,
    link: pdpLink(data?.needHelpPurchasingLink),
    // sends help center event to aptrinsic when user clicks on help center link
    onClick: () => {
      if (typeof window.aptrinsic === "function") {
        window.aptrinsic("track", "Trimble Help Center Event Rule", {
          accountId: state?.accountId
        });
      }
    }
  };
  const creditPolicyLink: CreditPolicyTextAndLink = {
    text: data?.creditPolicyText,
    link: pdpLink(data?.creditPolicyLink)
  };
  const questionsAndReturnsLink: CreditPolicyTextAndLink = {
    text: data?.questionsAndReturnsText,
    link: pdpLink(data?.questionsAndReturnsLink)
  };
  const regionBlockedUserLink = pdpLink(data?.regionBlockedUserLinkToLearnMore);
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: state?.currency ? state?.currency : "USD",
    currencyDisplay: "narrowSymbol"
  });
  const findPromoMatch = (promosFromAPI: any[] | undefined, promoCodesFromContentful: any[] | undefined) => {
    if (!promosFromAPI?.length) {
      return {
        selectedPromoFromAPI: undefined,
        matchedContentfulPromoCode: undefined
      };
    }

    // If there are no contentful entries for promos but an API promo exists, return the first API promo
    // When no matching contentful promo code is found, placeholder text will display "SAVINGS"
    if (!promoCodesFromContentful?.length) {
      return {
        selectedPromoFromAPI: promosFromAPI?.[0],
        matchedContentfulPromoCode: undefined
      };
    }
    const isPromoCodeMatch = (contentfulPromoCode: string | undefined, apiPromoCode: string | undefined) => contentfulPromoCode?.toLowerCase().trim() === apiPromoCode?.toLowerCase().trim();

    // Check if the contentful promo code matches the API promo code and return the matching API promo. If no match is found, return the first API promo to still display the sale price.
    const selectedPromoFromAPI = promosFromAPI?.find(apiPromo => promoCodesFromContentful?.some(contentfulPromo => isPromoCodeMatch(contentfulPromo?.discountCode, apiPromo?.promoName))) ?? promosFromAPI?.[0];

    // Check if the contentful promo code matches the API promo code and return the matching Contentful promo code
    const matchedContentfulPromoCode = promoCodesFromContentful?.find(contentfulPromo_0 => isPromoCodeMatch(contentfulPromo_0?.discountCode, selectedPromoFromAPI.promoName));
    return {
      selectedPromoFromAPI,
      matchedContentfulPromoCode
    };
  };
  const formatPricingCards = (): PricingCard[] => {
    const globalLabels = state?.globalEcommerceLabels;
    const yearLabel = globalLabels?.year || "year";
    const monthlyLabel = globalLabels?.monthly || "monthly";
    const perUserLabel = globalLabels?.perUser || "per user";
    return pricingCards.map((card_0: any) => {
      const basePrice = card_0?.price?.amount;
      const promoCodes = card_0?.promoCodesCollection?.items; // contentful promo codes
      const promosFromAPI_0 = card_0?.price?.marketingPromos; // api promo codes
      const hasMarketingPromo = promosFromAPI_0?.length > 0; // true if there are api promo codes

      const {
        selectedPromoFromAPI: selectedPromoFromAPI_0,
        matchedContentfulPromoCode: matchedContentfulPromoCode_0
      } = findPromoMatch(promosFromAPI_0, promoCodes);
      const salePrice = selectedPromoFromAPI_0?.amount; // the sale price from the api, returns amount even if no matching contentful promo code

      // Credits are sold as a quantity rather than a per-seat subscription, so
      // neither the Contentful copy nor the "per user" fallback applies to them.
      const isCreditsCard = card_0?.price?.cartLineItemCategory === "Credits";
      const productPriceDescription = card_0?.priceDescription?.trim();
      const resolvedPriceDescription = isCreditsCard ? null : productPriceDescription || perUserLabel || null;
      return {
        // displayName overrides the repeated Product Name on PDP product cards when set (MXP-2349)
        title: card_0?.displayName || card_0?.productName,
        terms: card_0?.billingTerms,
        price: hasMarketingPromo && salePrice ? formatter.format(salePrice) : basePrice ? formatter.format(basePrice) : "--.--",
        strikePrice: hasMarketingPromo && basePrice && salePrice ? formatter.format(basePrice) : null,
        // priceType:
        //   card?.annualVsMonthly === true
        //     ? yearLabel
        //     : card?.annualVsMonthly === false
        //       ? monthlyLabel
        //       : "",
        currency: state?.currency ? state?.currency : "---",
        maxPurchaseThreshold: card_0?.price?.maxPurchaseThreshold ?? null,
        // Trimble Credits Custom tab pulls these off pricingDetails.cards[0]
        minimumQuantity: card_0?.price?.minPurchaseThreshold ?? null,
        maximumQuantity: card_0?.price?.maxPurchaseThreshold ?? null,
        increment: card_0?.price?.quantityMultiplier ?? null,
        sku: card_0?.sku,
        priceDescription: resolvedPriceDescription,
        description: card_0?.purchaseTerms ?? null,
        imageURL: card_0?.listImage?.url ?? null,
        discount: hasMarketingPromo && matchedContentfulPromoCode_0?.displaySavings ? matchedContentfulPromoCode_0?.displaySavings : hasMarketingPromo && !matchedContentfulPromoCode_0?.displaySavings ? "SAVINGS" : null,
        // if on sale but no matching contentful promo code, show "SAVINGS"
        discountCode: hasMarketingPromo ? selectedPromoFromAPI_0?.promoName ?? null : null,
        featured: card_0?.featuredProduct ?? null,
        featuredText: card_0?.featuredText ?? null
      };
    });
  };

  // get pricing data for datalayer
  useEffect(() => {
    const getPriceForDataLayer = () => {
      const datalayerPrice = pricingCards?.map((card_1: any) => {
        if (card_1?.price?.amount) {
          return {
            id: card_1?.sys?.id,
            price: formatter.format(card_1?.price?.amount),
            currency: state?.currency
          };
        } else {
          return;
        }
        return {
          id: card_1?.sys?.id,
          price: formatter.format(card_1?.price?.amount),
          currency: state?.currency
        };
      });

      // if this method fires before the pricing API finishes, the array items will be undefined. We want to ignore this
      if (datalayerPrice.every(item => item === undefined)) {
        return;
      } else {
        setPricingDataForDatalayer(datalayerPrice);
      }
    };
    if (!pricingDataForDatalayer?.length && pricingCards && state?.currency && state?.countryCode && !loading) {
      getPriceForDataLayer();
    }
  }, [state?.currency, loading, pricingCards]);

  // Get Prices
  useEffect(() => {
    const getPricing = () => {
      const products = [data?.pim, ...(data?.secondaryProductsData ?? [])].filter(p => !!p);
      if (products.length > 0) {
        let isBlocked = false;

        // Reset pricing cards on currency change; this will remove the old prices
        setPricingCards(products);
        if (state?.countryCode) {
          setLoading(true);
          Promise.all(products.map(product => {
            return getPrice(state?.currency, product?.sku, state?.countryCode, setError).then(price => {
              if (price?.errorCode && price?.errorCode === "PRODUCTS_1607") {
                isBlocked = true;
              }

              // Create Trimble Credits pricing cards from the linked
              // Trimble Credits Pricing Cards entry (Top → Middle → Bottom).
              // Falls back to default tiers when the entry is missing.
              if (price?.cartLineItemCategory === "Credits" && typeof price?.amount === "number") {
                // Keep the original per-credit rate so the Custom tab can compute totalCost = customCredits * unit price.
                setCreditUnitAmount(price.amount);
                // Default the Custom tab's credit count to the API minimum the first time pricing loads.
                setCustomCredits(current => current ?? price?.minPurchaseThreshold ?? undefined);

                // Featured badge ("Most Popular") follows the Featured Card
                // checkboxes on the CMS entry. Other tiers explicitly null
                // out PIM featured fields so the badge only appears where selected.
                const creditsPricingCards = data?.trimbleCreditsPricingCards;
                const creditsTiers = resolveCreditsTiers(creditsPricingCards);
                const creditsNameLabel = creditsLabelsProp?.creditsNameLabel || "Credits";
                const creditCards: ProductDetails[] = creditsTiers.map((credits, tierIndex) => {
                  const isFeatured = isCreditsTierFeatured(creditsPricingCards, tierIndex);
                  return {
                    ...(product as ProductDetails),
                    sku: `${product?.sku}-${credits}`,
                    productName: `${credits.toLocaleString()} ${creditsNameLabel}`,
                    price: {
                      ...price,
                      amount: price.amount * credits
                    },
                    featuredProduct: isFeatured ? (product as any)?.featuredProduct ?? null : null,
                    featuredText: isFeatured ? (product as any)?.featuredText ?? null : null
                  };
                });
                setPricingCards(creditCards);
                return price;
              }
              setPricingCards((pricingCards_0: ProductDetails[]) => {
                return pricingCards_0?.map((card_2: ProductDetails) => {
                  return {
                    ...card_2,
                    price: card_2?.sku === product?.sku ? price : card_2?.price
                  };
                });
              });
              return price;
            });
          })).then(values => {
            setRegionBlocked(isBlocked);

            // Select product if only one product and it has a price
            if (products?.length === 1 && products?.[0]?.sku && values?.[0]) {
              setSelected(products?.[0]?.sku);
            }
            // setLoadDataLayer(true)
            setLoading(false);
          });
        }
      } else {
        setLoading(false);
      }
    };
    if (state?.currency) {
      getPricing();
    }
  }, [state?.currency, state?.countryCode, data?.trimbleCreditsPricingCards, creditsLabelsProp?.creditsNameLabel]);
  const pricingCardsFormatted = {
    cards: formatPricingCards()
  };

  // Credits PDP detection is driven by the price API's `cartLineItemCategory`
  // (set by the pricing service when a product is categorized as Credits)
  // rather than a hardcoded SKU. The actual SKU used for cart submissions is
  // just the PIM SKU on a credits PDP — per-tier SKUs only exist client-side
  // as `${pim.sku}-${tier}` to keep pricing cards distinguishable.
  const creditPage = pricingCards.some((c: ProductDetails) => c?.price?.cartLineItemCategory === "Credits");
  const creditsBaseSku = data?.pim?.sku;

  // Credits PDP list image logic
  const pimListImage = data?.pim?.listImage;
  const pimListImageUrl = pimListImage?.url;
  const listImage = creditPage && pimListImage?.url ? {
    src: pimListImage.url,
    altText: pimListImage.title || data?.pim?.productName || undefined
  } : null;
  const formatAndRenderMarkdown = markdownit();
  const finalProductDetailList = data?.longDescription ? formatAndRenderMarkdown.render(data.longDescription) : data?.pim?.longDescription ?? "";
  const labels = {
    quantityLabel: labelsProp?.quantityLabel ?? state?.globalEcommerceLabels?.quantityLabel,
    chooseYourPlanLabel: labelsProp?.chooseYourPlanLabel ?? data?.chooseYourPlanLabel,
    choosePlanLabel: labelsProp?.chooseYourPlanLabel ?? data?.chooseYourPlanLabel,
    numberOfLicensesLabel: labelsProp?.numberOfLicensesLabel ?? data?.numberOfLicensesLabel,
    showMoreLabel: labelsProp?.showMoreLabel ?? state?.globalEcommerceLabels?.showMoreLabel,
    showLessLabel: labelsProp?.showLessLabel ?? state?.globalEcommerceLabels?.showLessLabel,
    expandAllLabel: labelsProp?.expandAllLabel ?? state?.globalEcommerceLabels?.expandAllLabel,
    paymentMethodsLabel: labelsProp?.paymentMethodLabel ?? state?.globalEcommerceLabels?.paymentMethodLabel,
    regionLabel: state?.globalEcommerceLabels?.geoBadgeLabel,
    paymentFinePrint: labelsProp?.paymentFinePrintLabel ?? state?.globalEcommerceLabels?.paymentFinePrintLabel,
    ...(labelsProp ? {
      ...defaultLabels,
      ...labelsProp
    } : defaultLabels),
    ...(creditsLabelsProp ?? {}),
    creditPolicy: creditsLabelsProp?.creditPolicyLabel ?? undefined,
    minimumMaximumLabel: creditsLabelsProp?.minimummaximumLabel ?? undefined
  };
  useEffect(() => {
    const itemInCart_0 = state?.cart?.lineItems?.find((item_0: any) => {
      return item_0?.sku === addToCartSelectedProductCard(pricingCards, selected);
    });
    setItemInCart(itemInCart_0 ?? null);
  }, [state?.cart, data?.pim?.sku, selected]);
  useEffect(() => {
    const selectedCard = pricingCards.find(card_3 => card_3.sku === selected);
    if (count < 1 || count == 0) {
      setQuantityError(true);
      setErrorMessage(errorMessages?.errorMessageForQuantityAt0 ?? data?.errorMessageForQuantityAt0 ?? "");
    } else if (selectedCard?.price?.maxPurchaseThreshold !== null && selectedCard?.price?.maxPurchaseThreshold !== undefined && count > selectedCard.price.maxPurchaseThreshold) {
      setQuantityError(true);
      setErrorMessage(errorMessages?.errorMessageForMaxQuantity ?? data?.errorMessageForMaxQuantity ?? "");
    } else {
      setQuantityError(false);
      setErrorMessage("");
    }
  }, [count, state?.cart, selected, pricingCards, itemInCart, errorMessages, data]);
  const handleAddToCartSuccess = useCallback((data_0: any, sku: string) => {
    const selectedCard_0 = pricingCards.find((card_4: ProductDetails) => card_4.sku === sku);
    const lineItems = data_0?.lineItems?.map((itemInResponse: any) => {
      const isItemAdded = itemInResponse?.sku === sku;
      const itemInCart_1 = state?.cart?.lineItems?.find((item_1: any) => item_1?.sku === itemInResponse?.sku);
      // The cart API doesn't return `imageURL`. Credits rows always use the
      // PIM image (tier cards are client-only and the add SKU is the base PIM
      // SKU, so they never match). Other just-added rows use the PDP card;
      // preexisting rows keep whatever the local cart already had.
      // Credit-product field remapping mirrors useFlyoutCart.formatLineItems
      // so the row renders the CreditsTracker + bundle-total price.
      const imageURL = itemInResponse?.cartLineItemCategory === "Credits" ? pimListImageUrl : isItemAdded ?
      // Non-credits item that was just added: use the image from its PDP pricing card
      selectedCard_0?.listImage?.url :
      // Non-credits item that was already in the cart: keep its existing image
      itemInCart_1?.imageURL;
      return applyCreditProductMapping({
        ...itemInResponse,
        imageURL
      });
    });
    // Fall back to the cart line item's `name` because credits add-to-cart
    // rewrites the SKU to the base PIM SKU, which won't match any tier
    // pricing card (e.g. `<pim-sku>-10000`). The cart API response still
    // carries the human-readable product name (e.g. "Trimble Credits").
    const addedLineItem = lineItems?.find((item_2: any) => item_2?.sku === sku);
    const payload = {
      itemAdded: {
        name: selectedCard_0?.productName ?? addedLineItem?.name
      },
      ...data_0,
      lineItems
    };
    if (dispatch) {
      dispatch({
        type: "UPDATE_CART",
        payload: payload
      });
    }

    // Gainsight tracking
    const lineItem = data_0?.lineItems?.find((item_3: any) => item_3?.sku === sku);

    // sends add to cart event to aptrinsic
    if (typeof window.aptrinsic === "function") {
      window.aptrinsic("set", "globalContext", {
        contextType: "Add to Cart",
        cartId: data_0?.id,
        lineItemId: lineItem?.id,
        sku: sku,
        accountId: state?.accountId,
        accountName: state?.accountId
      });
    }

    // Google Analytics tracking
    const englishCard = (englishPricingCardsForDatalayer as DatalayerCard[])?.find(card_5 => {
      return card_5?.sku === sku;
    });

    // get ID from the cart API
    const updatedEnglishCard = lineItem && englishCard ? {
      ...englishCard,
      item_id: lineItem?.id
    } : englishCard;
    const dataLayerAddToCart = {
      ...updatedEnglishCard,
      item_category: "Software",
      affiliation: "Trimble.com",
      currency: lineItem?.totalPrice?.currencyCode,
      //USD
      quantity: count
    };
    pushDataLayerEvent({
      event: "add_to_cart",
      ecommerce: {
        item_list_name: "User Cart",
        item_list_id: data_0?.id,
        items: [dataLayerAddToCart]
      }
    }, {
      clearEcommerce: true
    });
    track("add_to_cart", {
      product_name: selectedCard_0?.productName ?? "",
      sku,
      price: lineItem?.totalPrice?.amount ?? 0,
      currency: lineItem?.totalPrice?.currencyCode ?? ""
    });
  }, [count, dispatch, englishPricingCardsForDatalayer, pimListImageUrl, pricingCards, state?.cart?.lineItems, track]);

  // URL-param auto-add-to-cart (e.g. ?sku=ABC&quantity=2)
  useEffect(() => {
    if (state?.isCartInitialized && !loading && !isAddedToCart) {
      const searchParams = new URLSearchParams(window.location.search);
      const sku_0 = searchParams.get("sku");
      const quantity = searchParams.get("quantity");
      const anonymousCartId = localStorage?.getItem("cartId");
      if (sku_0 && quantity && state?.currency && state?.countryCode) {
        if (state?.countryCode === "US" || state?.countryCode === "CA") {
          const TIDAuth = getTIDAuth();
          const isRegistered = isOwner && isDVW;
          addToCart(state?.currency, state?.countryCode, sku_0, setError, Number(quantity), setCount, !!anonymousCartId, state?.cart, state?.accountId, isRegistered ? window.sessionStorage.getItem("token") : TIDAuth?.id, setIsProcessing, handleAddToCartSuccess, setIsOpen, setSubscriptionTypeErrorNonMerge, setAddToCartError, setIsMaxQuantityError, !isRegistered, true);
          setIsAddedToCart(true);
        } else {
          setIsRestrictedError(true);
          setIsOpen(true);
        }
      }
    }
  }, [state?.currency, state?.countryCode, state?.isCartInitialized, state?.accountId, isAddedToCart, loading]);
  const sections = data?.sectionsCollection?.items?.map((section: Section) => ({
    title: section?.title,
    description: section?.description,
    link: handleButtons(section?.linkCollection?.items, true, locale)?.map((data_1: any) => ({
      text: data_1?.label,
      url: data_1?.destination
    }))?.[0],
    items: section?.itemsCollection?.items?.map((item_4: AccordionRichTextItem) => ({
      headline: item_4?.headline,
      description: item_4?.description?.json ? documentToHtmlString(item_4?.description?.json, richTextOptions) : null
    }))
  }));

  // Return null when there are no media sections so terra's `{inlineMedia && ...}`
  // guard collapses the wrapper div (and the empty `<InlineMedia>` wrappers
  // inside). Previously this always returned a truthy object.
  const formatInlineMedia = (inlineMedia?: InlineMediaData | null): FormattedInlineMedia | null => {
    const sections_0 = inlineMedia?.mediaSectionsCollection?.items;
    if (!sections_0?.length) return null;
    return {
      eyebrow: inlineMedia?.eyebrow,
      header: inlineMedia?.header,
      variant: "tabs",
      inlineMediaPanels: sections_0.map(mediaSection => ({
        publicTitle: mediaSection?.title,
        description: mediaSection?.description ? documentToHtmlString(mediaSection?.description?.json, richTextOptions) : null,
        alignment: "mediaRight" as const,
        media: mediaSection?.mediaCollection?.items?.map(media => ({
          type: "video" as const,
          videoURL: resolveInlineMediaVideoUrl(media),
          assetURL: media?.videoThumbnail?.image?.url ?? undefined,
          alt: media?.videoThumbnail?.altText ?? ""
        })),
        buttons: (handleButtons(mediaSection?.buttonsCollection?.items, true, locale) ?? []).map((button: any): FormattedInlineMediaButton => ({
          ...button,
          primary: false,
          // designs always show the non primary button
          size: "large"
        }))
      }))
    };
  };

  // Formats the total cost for the Trimble Credits custom tab by combining 
  // the per-credit price from the API and the user's selected credit count.
  const totalCost = creditUnitAmount !== null && typeof customCredits === "number" ? formatter.format(creditUnitAmount * customCredits) : undefined;
  const handleReplaceCart = async () => {
    try {
      const isRegisteredCart = isOwner && isDVW;
      const authToken = await getValidToken();
      if (!authToken && isRegisteredCart) {
        setAddToCartError(true);
        setIsOpen(true);
        return;
      }
      const url = isRegisteredCart ? `${productsEndpoint}/carts` : `${productsEndpoint}/carts/${state?.cart?.id}`;
      const headers: HeadersInit = isRegisteredCart ? {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        "x-account-id": state?.activeAccount?.id
      } : {
        "Content-Type": "application/json",
        Accept: "application/json"
      };

      // Determine if the selected SKU is a credits tier SKU or the custom tab state. If it is the custom tab state, we need to use the base credits SKU and the custom credits count. If it is a credits tier SKU, we need to use the base credits SKU and the tier number.
      const isCreditsTierSku = !!creditsBaseSku && selected?.startsWith(`${creditsBaseSku}-`);
      const isCustomCredits = creditPage && !isCreditsTierSku && typeof customCredits === "number";
      const skuForReplace = isCreditsTierSku || isCustomCredits ? creditsBaseSku : selected;
      const quantityForReplace = isCreditsTierSku ? count * parseInt(selected!.slice((creditsBaseSku as string).length + 1), 10) : isCustomCredits ? customCredits as number : count;
      let actionItems = [];

      // ITEM TO ADD
      actionItems.push({
        productSku: skuForReplace,
        updateAction: "ADD_CART_LINEITEM",
        quantity: quantityForReplace
      });

      // CART ITEMS TO REMOVE
      state?.cart?.lineItems?.forEach((item_5: any) => actionItems.push({
        updateAction: "REMOVE_CART_LINEITEM",
        lineitemId: item_5?.id
      }));
      setIsOpen(false);
      setCartConflict(false);
      setIsProcessing(true);
      const response = await fetch(url, {
        method: isRegisteredCart ? "PATCH" : "PUT",
        headers,
        body: JSON.stringify({
          lineItems: actionItems
        })
      });
      const responseData = await response.json();
      setIsProcessing(false);
      if (response.ok) {
        handleAddToCartSuccess(responseData, skuForReplace ?? selected);
      } else {
        console.error("Failed to replace cart", responseData);
        setAddToCartError(true);
        setIsOpen(true);
        return;
      }
    } catch (error) {
      console.error("An error occurred while replacing the cart", error);
      setIsProcessing(false);
      setAddToCartError(true);
      setIsOpen(true);
      return;
    }
  };
  const handleCancelReplaceCart = () => {
    setCartConflict(false);
    setIsOpen(false);
  };
  const secondaryLinkButton = handleButtons(data?.secondaryLink ? [data.secondaryLink] : [])?.[0];
  const secondaryLink = secondaryLinkButton?.destination && secondaryLinkButton?.label ? {
    text: data?.secondaryLinkText,
    link: {
      text: secondaryLinkButton.label,
      url: resolveHref(secondaryLinkButton.destination, locale)
    }
  } : undefined;
  return {
    pricingCards,
    creditPage,
    listImage,
    customTab: {
      credits: customCredits,
      onCreditsChange: setCustomCredits,
      totalCost,
      onCreditTabChange: tab => {
        if (tab === "custom") setSelected("");
      }
    },
    locale,
    sections: sections,
    pricingDetails: pricingCardsFormatted,
    eyebrow: data?.eyebrow ?? "",
    subheader: data?.subheader,
    productName: data?.productName ?? data?.pim?.productName,
    productDetailList: finalProductDetailList,
    images: images,
    needHelpPurchasing: needHelpPurchasing,
    questionsAndReturnsLink: questionsAndReturnsLink,
    creditPolicyLink: creditPolicyLink,
    regionBlockedUserLink,
    labels: labels,
    regionAvailability: data?.regionalAvailability,
    productDescription: data?.productDescription,
    productOverview: data?.productOverview,
    productDetails: formatProductDetails(),
    inlineMedia: formatInlineMedia(data?.inlineMedia),
    handleReplaceCart,
    handleCancelReplaceCart,
    addToCartButton: {
      text: labelsProp?.addToCartLabel ?? data?.addToCartLabel,
      processingText: labelsProp?.processingLabel ?? data?.processingLabel,
      onClick: async () => {
        const selectedCard_1 = pricingCards.find(card_6 => card_6.sku === selected);
        // On the Custom tab no tier card is selected, so `selectedCard` is
        // undefined. Every credits tier card is a variant of the same base
        // SKU and shares the same `cartLineItemCategory`, so fall back to
        // any pricing card on the credits page so the conflict dialog
        // still fires (and price-derived checks still work).
        const conflictCheckCard = selectedCard_1 ?? (creditPage ? pricingCards[0] : undefined);

        // Check quantity against max threshold before attempting to add to cart
        if (itemInCart?.quantity && selectedCard_1?.price?.maxPurchaseThreshold && itemInCart.quantity + count > selectedCard_1.price.maxPurchaseThreshold) {
          setQuantityError(true);
          setErrorMessage(errorMessages?.errorMessageForMaxQuantity ?? data?.errorMessageForMaxQuantity ?? "");
          return;
        }

        // Check for cart conflicts - if there are items in the cart that have a different cartLineItemCategory than the selected product, we know there's a conflict and we need to show the user the conflict dialog
        const hasConflictingItemInCart = state?.cart?.lineItems?.length > 0 && state?.cart?.lineItems?.some((item_6: any) => conflictCheckCard?.price?.cartLineItemCategory && conflictCheckCard?.price?.cartLineItemCategory !== item_6?.cartLineItemCategory);
        if (hasConflictingItemInCart) {
          setCartConflict(true);
          setIsOpen(true);
          return;
        }

        // Trimble Credits ONLY (price.cartLineItemCategory === "Credits"):
        // there are two purchase paths on the credit PDP:
        //   1. Packs tab — the user picks a hardcoded tier card whose SKU
        //      is "<pim.sku>-<tier>" (e.g. "Credits-Test-SKU-10000").
        //      The cart SKU is stripped back to the base PIM SKU (the
        //      per-tier SKUs only exist on the client for the pricing
        //      cards) and the quantity is `count * tier`.
        //   2. Custom tab — no card is selected; the CreditsTracker drives
        //      `customCredits` and that value is the quantity. The cart
        //      SKU is the base PIM SKU.
        // In both cases, if the user already has credits in the cart we
        // add the new amount to the existing line-item quantity ourselves
        // and pass `isQuantityReplacement = true`
        // Every other SKU falls through unchanged.
        const isCreditsTierSku_0 = !!creditsBaseSku && selected?.startsWith(`${creditsBaseSku}-`);
        const isCustomCredits_0 = creditPage && !isCreditsTierSku_0 && typeof customCredits === "number";
        const skuForCart = isCreditsTierSku_0 || isCustomCredits_0 ? creditsBaseSku : selected;
        let quantityForCart = count.toString();
        let isQuantityReplacement: boolean | null = null;
        if (isCreditsTierSku_0 || isCustomCredits_0) {
          const addedCredits = isCreditsTierSku_0 ? count * parseInt(selected!.slice((creditsBaseSku as string).length + 1), 10) : customCredits as number;
          const existingCreditsLine = state?.cart?.lineItems?.find((item_7: any) => item_7?.cartLineItemCategory === "Credits");
          const existingCredits = existingCreditsLine?.quantity ?? 0;
          quantityForCart = (existingCredits + addedCredits).toString();
          isQuantityReplacement = true;
        }
        const authToken_0 = await getValidToken();
        const anonymousCartId_0 = localStorage?.getItem("cartId");
        const isRegistered_0 = isOwner && isDVW;
        setIsProcessing(true);
        addToCart(state?.currency, state?.countryCode, skuForCart, setError, quantityForCart, setCount, !!anonymousCartId_0, state?.cart, state?.accountId, authToken_0, setIsProcessing, handleAddToCartSuccess, setIsOpen, setSubscriptionTypeErrorNonMerge, setAddToCartError, setIsMaxQuantityError, !isRegistered_0,
        // IS PRODUCT USER
        isQuantityReplacement);
      }
    },
    usageTerms: {
      title: data?.usageTermsTitle,
      description: documentToReactComponents(data?.usageTermsDescription?.json as any, getRichTextReactOptions(data?.usageTermsDescription?.links?.entries?.hyperlink, locale))
    },
    secondaryLink
  };
};
export default useProductDetail;